import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { 
  Code2, 
  Layers,
  Zap,
  Shield,
  Settings,
  FileCode2
} from 'lucide-react'

interface TechnicalFeature {
  id: string
  icon: React.ElementType
  title: string
  description: string
  details: string[]
}

interface ArchitectureLayer {
  id: string
  name: string
  description: string
  components: string[]
}

export const ComparisonSection: React.FC = () => {
  const { t } = useTranslation('technology')
  
  const technicalFeatures: TechnicalFeature[] = [
    {
      id: 'hybrid-architecture',
      icon: Layers,
      title: t('technicalFeatures.hybridArchitecture.title'),
      description: t('technicalFeatures.hybridArchitecture.description'),
      details: t('technicalFeatures.hybridArchitecture.details', { returnObjects: true }) as string[]
    },
    {
      id: 'dynamic-engine',
      icon: Code2,
      title: t('technicalFeatures.dynamicEngine.title'),
      description: t('technicalFeatures.dynamicEngine.description'),
      details: t('technicalFeatures.dynamicEngine.details', { returnObjects: true }) as string[]
    },
    {
      id: 'hot-deployment',
      icon: Zap,
      title: t('technicalFeatures.hotDeployment.title'),
      description: t('technicalFeatures.hotDeployment.description'),
      details: t('technicalFeatures.hotDeployment.details', { returnObjects: true }) as string[]
    },
    {
      id: 'meta-programming',
      icon: Settings,
      title: t('technicalFeatures.metaProgramming.title'),
      description: t('technicalFeatures.metaProgramming.description'),
      details: t('technicalFeatures.metaProgramming.details', { returnObjects: true }) as string[]
    },
    {
      id: 'multi-framework',
      icon: FileCode2,
      title: t('technicalFeatures.multiFramework.title'),
      description: t('technicalFeatures.multiFramework.description'),
      details: t('technicalFeatures.multiFramework.details', { returnObjects: true }) as string[]
    },
    {
      id: 'enterprise-grade',
      icon: Shield,
      title: t('technicalFeatures.enterpriseGrade.title'),
      description: t('technicalFeatures.enterpriseGrade.description'),
      details: t('technicalFeatures.enterpriseGrade.details', { returnObjects: true }) as string[]
    }
  ]

  const architectureLayers: ArchitectureLayer[] = [
    {
      id: 'backend',
      name: t('architecture.backend.name'),
      description: t('architecture.backend.description'),
      components: t('architecture.backend.components', { returnObjects: true }) as string[]
    },
    {
      id: 'frontend',
      name: t('architecture.frontend.name'),
      description: t('architecture.frontend.description'),
      components: t('architecture.frontend.components', { returnObjects: true }) as string[]
    },
    {
      id: 'integration',
      name: t('architecture.integration.name'),
      description: t('architecture.integration.description'),
      components: t('architecture.integration.components', { returnObjects: true }) as string[]
    }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-4xl mx-auto"
          >
            {t('subtitle')}
          </motion.p>
        </div>

        {/* Architecture Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            {t('architecture.title')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {architectureLayers.map((layer, index) => (
              <motion.div
                key={layer.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl shadow-lg border border-gray-200"
              >
                <h4 className="text-lg font-bold text-gray-900 mb-3">{layer.name}</h4>
                <p className="text-gray-600 mb-4 text-sm">{layer.description}</p>
                <ul className="space-y-2">
                  {layer.components.map((component, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-kylix-secondary rounded-full"></div>
                      <span className="text-gray-700">{component}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technical Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            {t('features.title')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technicalFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-kylix-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-kylix-secondary" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 mb-4 text-sm">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-kylix-secondary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Framework Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-kylix-primary to-kylix-secondary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">{t('benefits.title')}</h3>
            <p className="text-xl mb-6 opacity-90">{t('benefits.description')}</p>

          </div>
        </motion.div>
      </div>
    </section>
  )
}